import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { ButtonComponent } from '@shared/components/button/button.component';
import {
  ConfirmDialogService,
  ConfirmDialogVariant
} from './confirm-dialog.service';

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [CommonModule, ButtonComponent],
  template: `
    <div
      *ngIf="isOpen"
      class="pe-confirm-overlay"
      role="dialog"
      aria-modal="true"
      [attr.aria-labelledby]="'pe-confirm-title'">
      <div class="pe-confirm-card" (click)="$event.stopPropagation()">
        <div class="pe-confirm-icon" [class.is-danger]="variant === 'danger'" [class.is-info]="variant !== 'danger'">
          <svg *ngIf="variant === 'danger'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <svg *ngIf="variant !== 'danger'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
        </div>
        <h2 id="pe-confirm-title" class="pe-confirm-title">{{ title }}</h2>
        <p class="pe-confirm-message">{{ message }}</p>
        <div class="pe-confirm-actions">
          <app-button variant="ghost" (buttonClick)="onCancel()">{{ cancelLabel }}</app-button>
          <app-button
            [variant]="variant === 'danger' ? 'danger' : 'primary'"
            (buttonClick)="onConfirm()">
            {{ confirmLabel }}
          </app-button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .pe-confirm-overlay {
      position: fixed;
      inset: 0;
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1.5rem;
      background: rgba(15, 23, 42, 0.48);
      backdrop-filter: blur(8px);
      isolation: isolate;
    }
    .pe-confirm-card {
      position: relative;
      z-index: 1;
      width: 100%;
      max-width: 26.5rem;
      background: #ffffff;
      border-radius: 1.25rem;
      padding: 2rem 1.75rem 1.5rem;
      box-shadow: 0 24px 64px rgba(15, 23, 42, 0.22);
      border: 1px solid #e2e8f0;
      text-align: center;
    }
    .pe-confirm-icon {
      width: 3.5rem;
      height: 3.5rem;
      border-radius: 999px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1.125rem;
    }
    .pe-confirm-icon svg { width: 1.75rem; height: 1.75rem; }
    .pe-confirm-icon.is-danger { background: #fef2f2; color: #dc2626; }
    .pe-confirm-icon.is-info { background: #eff6ff; color: #2563eb; }
    .pe-confirm-title {
      margin: 0 0 0.5rem;
      font-size: 1.125rem;
      font-weight: 700;
      letter-spacing: -0.02em;
      color: #0f172a;
    }
    .pe-confirm-message {
      margin: 0 0 1.5rem;
      font-size: 0.9375rem;
      line-height: 1.55;
      color: #64748b;
    }
    .pe-confirm-actions {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
    }
  `]
})
export class ConfirmDialogComponent implements OnInit, OnDestroy {
  private confirmDialogService = inject(ConfirmDialogService);
  private destroy$ = new Subject<void>();
  private resolve?: (confirmed: boolean) => void;

  isOpen = false;
  title = '';
  message = '';
  confirmLabel = 'Confirm';
  cancelLabel = 'Cancel';
  variant: ConfirmDialogVariant = 'default';

  ngOnInit(): void {
    this.confirmDialogService.request$
      .pipe(takeUntil(this.destroy$))
      .subscribe(request => {
        if (!request) {
          this.close(false);
          return;
        }

        this.title = request.options.title;
        this.message = request.options.message;
        this.confirmLabel = request.options.confirmLabel;
        this.cancelLabel = request.options.cancelLabel;
        this.variant = request.options.variant;
        this.resolve = request.resolve;
        this.isOpen = true;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onConfirm(): void {
    this.close(true);
  }

  onCancel(): void {
    this.close(false);
  }

  private close(confirmed: boolean): void {
    if (!this.isOpen && !this.resolve) {
      return;
    }
    this.isOpen = false;
    const resolve = this.resolve;
    this.resolve = undefined;
    resolve?.(confirmed);
  }
}
